import { TenantJob } from '~/tenants/common/TenantJob';
import { BreOrderContext, BrePerformableConfig } from '~/tenants/bre/model/BreOrderContext';
import BrePhotosConfig from '~/tenants/bre/performable/photos/BrePhotosConfig';
import BreWebsiteConfig from '~/tenants/bre/performable/website/BreWebsiteConfig';
import BreFloorplanConfig from '~/tenants/bre/performable/floorplan/BreFloorplanConfig';
import BreBoostConfig from '~/tenants/bre/performable/boost/BreBoostConfig';
import BreSocialConfig from '~/tenants/bre/performable/social/BreSocialConfig';
import BreDuskConfig from '~/tenants/bre/performable/dusk/BreDuskConfig';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { addressToFull, DisplayData } from '~/lib/model';
import BreAerialConfig from '~/tenants/bre/performable/aerial/BreAerialConfig';
import BreCinematicConfig from '~/tenants/bre/performable/cinematic/BreCinematicConfig';
import BreMatterportConfig from '~/tenants/bre/performable/matterport/BreMatterportConfig';
import BreTwilightConfig from '~/tenants/bre/performable/twilight/BreTwilightConfig';

export default abstract class BreJob<P extends BrePerformableConfig = BrePerformableConfig> extends TenantJob<
  BreOrderContext,
  P
> {
  protected get orderContainsPhotos(): boolean {
    return this.order.jobs.some((j) => j.performable_id === BrePhotosConfig.id);
  }

  protected get orderContainsAerial(): boolean {
    return this.order.jobs.some((j) => j.performable_id === BreAerialConfig.id);
  }

  protected get orderContainsCinematic(): boolean {
    return this.order.jobs.some((j) => j.performable_id === BreCinematicConfig.id);
  }

  protected get orderContainsMatterport(): boolean {
    return this.order.jobs.some((j) => j.performable_id === BreMatterportConfig.id);
  }

  protected get orderContainsTwilight(): boolean {
    return this.order.jobs.some((j) => j.performable_id === BreTwilightConfig.id);
  }

  protected get orderContainsSocialPackage(): boolean {
    const hasPhotos = this.order.jobs.some((j) => j.performable_id === BrePhotosConfig.id && j.metadata.photos >= 15);
    const hasWebsite = this.order.jobs.some(
      (j) => j.performable_id === BreWebsiteConfig.id && j.metadata.type === 'website',
    );
    const hasFloorplans = this.order.jobs.some((j) => j.performable_id === BreFloorplanConfig.id);
    const hasBoost = this.order.jobs.some((j) => j.performable_id === BreBoostConfig.id);
    const hasSocial = this.order.jobs.some((j) => j.performable_id === BreSocialConfig.id);
    const hasDusk = this.order.jobs.some((j) => j.performable_id === BreDuskConfig.id);

    return hasPhotos && hasWebsite && hasFloorplans && hasBoost && hasSocial && hasDusk;
  }

  info(): DisplayData[] {
    const info = super.info();

    if (this.order.metadata.delivery && this.isDelivery()) {
      info.push({
        name: 'Delivery',
        value: addressToFull(this.order.metadata.delivery),
        provider: true,
      });
    }

    return info;
  }

  protected get isMarketing(): boolean {
    const { type } = this.order.metadata;
    return type === BreOrderType.AGENT_MKTG || type === BreOrderType.PROPERTY_MKTG;
  }

  get sqft(): number {
    const { type } = this.order.metadata;
    return type !== BreOrderType.AGENT_MKTG && type !== BreOrderType.PROPERTY_MKTG ? this.order.metadata.sqft : 0;
  }

  isOneOf(...types: BreOrderType[]): boolean {
    const { type } = this.order.metadata;
    return types.includes(type);
  }

  isDelivery(): boolean {
    return false;
  }

  isPrint(): boolean {
    return false;
  }

  isFreeDelivery(): boolean {
    return false;
  }
}
